import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ListTodo, ArrowRight } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { TaskItem } from "@/components/tasks/TaskItem";
import { QuickAddTask } from "@/components/tasks/QuickAddTask";
import { TaskDialog } from "@/components/tasks/TaskDialog";
import { fetchTasks, TASKS_CHANGED } from "@/lib/tasks";

/** Opne huskepunkt knytte til denne maskina + hurtiginput. */
export const MachineTasks = ({ machine }) => {
  const navigate = useNavigate();
  const [items, setItems] = useState(null);
  const [dialog, setDialog] = useState({ open: false, task: null, defaults: null });
  const machineId = machine?.id;
  const defaults = useMemo(() => ({ machine_id: machineId, customer_id: machine?.customer_id || "" }), [machineId, machine?.customer_id]);

  const load = useCallback(() => {
    if (!machineId) return;
    fetchTasks({ machine_id: machineId, status: "open", limit: 50 }).then((d) => setItems(d.items || [])).catch(() => setItems([]));
  }, [machineId]);

  useEffect(() => {
    load();
    window.addEventListener(TASKS_CHANGED, load);
    return () => window.removeEventListener(TASKS_CHANGED, load);
  }, [load]);

  const onChange = (updated, original) => {
    setItems((prev) => {
      if (!prev) return prev;
      if (!updated) return prev.filter((t) => t.id !== original.id);
      if (updated.status === "done") return prev.filter((t) => t.id !== updated.id);
      return prev.map((t) => (t.id === updated.id ? updated : t));
    });
  };

  return (
    <>
      <Card className="shadow-sm" data-testid="machine-tasks">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
          <div>
            <CardTitle className="flex items-center gap-2 font-heading text-base font-bold"><ListTodo className="h-4 w-4 text-primary" /> Huskeliste for maskina {items && items.length > 0 && <span className="rounded-full bg-muted px-1.5 text-xs tabular-nums">{items.length}</span>}</CardTitle>
            <p className="mt-0.5 text-xs text-muted-foreground">Ting som skal gjerast utanom service – synleg for alle.</p>
          </div>
          <Button variant="ghost" size="sm" onClick={() => navigate(`/huskeliste?machine=${machineId}`)} data-testid="machine-tasks-all">Alle <ArrowRight /></Button>
        </CardHeader>
        <CardContent className="space-y-3 pt-0">
          <QuickAddTask defaults={defaults} placeholder="t.d. Sjekk lekkasje ved neste besøk" testid="machine-task-quick-add" onMore={(title) => setDialog({ open: true, task: null, defaults: { ...defaults, title } })} />
          {items === null ? (
            <div className="space-y-2">{[...Array(2)].map((_, i) => <Skeleton key={i} className="h-12 w-full" />)}</div>
          ) : items.length === 0 ? (
            <p className="px-1 text-sm text-muted-foreground" data-testid="machine-tasks-empty">Ingen opne punkt for denne maskina.</p>
          ) : (
            <div className="-mx-1 divide-y divide-border">
              {items.map((t) => (
                <TaskItem key={t.id} task={t} compact showLinks={false} onEdit={(task) => setDialog({ open: true, task, defaults: null })} onChange={(u) => onChange(u, t)} />
              ))}
            </div>
          )}
        </CardContent>
      </Card>
      <TaskDialog open={dialog.open} onOpenChange={(o) => setDialog((d) => ({ ...d, open: o }))} task={dialog.task} defaults={dialog.defaults} onSaved={load} />
    </>
  );
};
